import moment from "moment";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNull, isUndefined } from "underscore";
import { IDataTableRow } from "../../../../components/DataTable/DataTableBody/DataTableBody";
import TruncatedText from "../../../../components/TruncatedText/TruncatedText";
import {
    DataTableContextFilters,
    useDataTableContext,
} from "../../../../components/DataTable/DataTableContext/DataTableContextProvider";
import {
    FilterType,
    IFilteredColumn,
} from "../../../../components/DataTable/DataTableFilters/DataTableFilters";
import DelegeeStatus, { Statuses } from "../../../../components/DelegeeStatus/DelegeeStatus";
import { FmpFilters } from "../../../../constants/filtering.constants";
import useAppContext from "../../../../context/hooks/useAppContext";
import useFleetManagerContext from "../../../../context/hooks/useFleetManagerContext";
import useMyFleetContext from "../../../../context/hooks/useMyFleetContext";
import { getParkingProductOptions } from "../../../../helpers/selectOptionHelper";
import { useOnBulkImportFinishedCallback } from "../../../../hooks/useOnBulkImportFinishedCallback";
import { DelegeeWithProductsModel } from "../../../../models/delegations/DelegatedParkingProductsModel";
import {
    DelegatedEntryRight,
    FlatDelegee,
} from "../../../../models/delegations/DelegatedEntryRight";
import { WithFiltersAndPaging } from "../../../../models/filters/WithFiltersAndPaging";
import { ForFleetManager } from "../../../../models/ForFleetManager";
import { ContractType, ParkingProductType } from "../../../../models/ParkingProductModel";
import * as entryRightsService from "../../../../services/entryRights.service";
import { useAppDispatch, useAppSelector } from "../../../../store";
import {
    getEntryRightDelegeesMyFleetOverviewThunk,
    toggleShowRevokeEntryRightsAction,
} from "../../../../store/actions/entryRights.actions";
import * as parkingProductsThunks from "../../../../store/actions/parkingProducts.thunks";
import FleetManagerDataTable, {
    IFleetManagerDataTableModel,
} from "../../FleetManagerDataTable/FleetManagerDataTable";
import { toProductsJsx } from "../../Helpers/ParkingRightsHelpers";
import filteredColumnObjectBuilder from "../../MyProducts/ObjectBuilders/FilteredColumnObjectBuilder";
import {
    EntryRightProductKey,
    isEntryRightsMyProductsRowKeyArray,
    selectEnableDelegationsScheduling,
    useSharedProductsMenuAction,
} from "../../MyProducts/ParkingRightsDataTable/common";
import { getSelectedParkingRightIds } from "../MyFleetHelpers";

export interface IDelegeeDataTableModel extends IFleetManagerDataTableModel {
    rowKey: string | EntryRightProductKey;
    products: JSX.Element | null;
    activationDate?: string | null;
    expirationDate?: string | null;
}

interface IDelegeesDataTable {
    subHeading?: ReactNode;
    productType: ParkingProductType;
}

const createGroupedData = (delegeeWithRights: DelegeeWithProductsModel): IDelegeeDataTableModel => {
    return {
        rowKey: delegeeWithRights.registrationId,
        name: <TruncatedText text={delegeeWithRights.name} />,
        email: <TruncatedText text={delegeeWithRights.emailAddress} />,
        products: toProductsJsx(delegeeWithRights.products),
        invitationDate: moment(delegeeWithRights.invitationDate).format("DD/MM/YYYY"),
        status: (
            <DelegeeStatus
                type={delegeeWithRights.invitationAccepted ? Statuses.Accepted : Statuses.Pending}
            />
        ),
    };
};

const createOverviewData = (delegatedEntryRight: FlatDelegee): IDelegeeDataTableModel => {
    return {
        rowKey: {
            delegeeId: delegatedEntryRight.delegee.id,
            entryRightId: delegatedEntryRight.product?.entryRightId ?? undefined,
            pmcId: delegatedEntryRight.product?.pmcId ?? undefined,
        },
        name: <TruncatedText text={delegatedEntryRight.delegee.name} />,
        email: <TruncatedText text={delegatedEntryRight.delegee.email} />,
        products: delegatedEntryRight.product
            ? toProductsJsx([
                  {
                      parkingRightId: delegatedEntryRight.id,
                      id: delegatedEntryRight.product.pmcId,
                      pmcId: delegatedEntryRight.product.pmcId,
                      name: delegatedEntryRight.product.pmcName,
                      location: delegatedEntryRight.product.locationName,
                      created: delegatedEntryRight.invitationDate,
                      status: delegatedEntryRight.status,
                      contractType: ContractType.SeasonTicket,
                      productType: ParkingProductType.PedestrianAccess,
                  },
              ])
            : null,
        invitationDate: delegatedEntryRight.invitationDate
            ? moment.parseZone(delegatedEntryRight.invitationDate).format("DD/MM/YYYY")
            : "",
        activationDate: delegatedEntryRight.activationDate
            ? moment.parseZone(delegatedEntryRight.activationDate).format("DD/MM/YYYY")
            : null,
        expirationDate: delegatedEntryRight.expirationDate
            ? moment.parseZone(delegatedEntryRight.expirationDate).format("DD/MM/YYYY")
            : null,
        status: <DelegeeStatus type={delegatedEntryRight.status} />,
    };
};

const DelegeesDataTable = (props: IDelegeesDataTable) => {
    const { productType } = props;
    const dispatch = useAppDispatch();
    const parkingProducts = useAppSelector((state) => state.parkingProducts);
    const groupedParkingDelegees = useAppSelector((state) => state.parkingProducts.delegees);
    const groupedEntryRightDelegees = useAppSelector((state) => state.entryRights.myFleet.delegees);
    const entryRightOverview = useAppSelector((state) => {
        switch (productType) {
            case ParkingProductType.PedestrianAccess:
                return state.entryRights.myFleetOverview.pedestrians;
            case ParkingProductType.ParkingRights:
                return state.entryRights.myFleetOverview.parkingRights;
        }
    });
    const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);
    const { t } = useTranslation(["myFleet", "globals", "dataTable", "fleetManagerTable"]);

    const {
        fleetManagerState,
        toggleBatchRevokeParkingRightsWarning,
        toggleBatchDeleteDelegationsWarning,
    } = useFleetManagerContext();
    const { selectedRowKeys } = fleetManagerState;
    const toggleRevokeEntryRightsWarning = () => dispatch(toggleShowRevokeEntryRightsAction());

    const dataTableContext = useDataTableContext();
    const myFleetContext = useMyFleetContext();
    const { appState } = useAppContext();
    const onRevokeClicked = () => {
        if (
            productType === ParkingProductType.PedestrianAccess ||
            (productType === ParkingProductType.ParkingRights && enableDelegationsScheduling)
        ) {
            toggleRevokeEntryRightsWarning();
            return;
        }
        if (productType === ParkingProductType.ParkingRights) {
            toggleBatchRevokeParkingRightsWarning();
        }
    };
    const onDeleteClicked = () => {
        toggleBatchDeleteDelegationsWarning();
    };

    const getRequestModel = (): WithFiltersAndPaging<ForFleetManager> => {
        return {
            entity: {
                seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
                language: appState.selectedLanguage,
            },
            pagination: {
                pageNumber: dataTableContext.state.pageNumber!,
                pageSize: dataTableContext.state.pageSize!,
            },
            filters: [
                {
                    key: FmpFilters.sortBy,
                    type: FilterType.QueryParam,
                    value: myFleetContext.myFleetContextState.sortBy,
                },
                {
                    key: FmpFilters.orderDesc,
                    type: FilterType.QueryParam,
                    value: myFleetContext.myFleetContextState.orderDesc?.toString(),
                },
                ...(dataTableContext.state.filters as DataTableContextFilters[]),
            ],
        };
    };

    const selectedParkingRightsCount = useMemo(() => {
        if (selectedRowKeys.length === 0) {
            return 0;
        }

        if (isEntryRightsMyProductsRowKeyArray(selectedRowKeys)) {
            return selectedRowKeys.reduce((acc, rowKey) => {
                if (rowKey.entryRightId) {
                    return acc + 1;
                }
                return acc;
            }, 0);
        }
        return getSelectedParkingRightIds(
            fleetManagerState,
            dataTableContext.state,
            productType === ParkingProductType.PedestrianAccess
                ? groupedEntryRightDelegees
                : groupedParkingDelegees
        ).length;
    }, [
        dataTableContext.state,
        groupedEntryRightDelegees,
        groupedParkingDelegees,
        selectedRowKeys,
        productType,
    ]);

    const fetchDelegees = () => {
        if (!appState.user.seasonTicketOwnerCrmId) {
            return;
        }

        const request = getRequestModel();

        if (enableDelegationsScheduling) {
            dispatch(
                getEntryRightDelegeesMyFleetOverviewThunk({
                    request,
                    productContractTypeFilter: { productType },
                })
            );
            return;
        }

        if (productType === ParkingProductType.PedestrianAccess) {
            dispatch(
                entryRightsService.getEntryRightDelegeesMyFleet(request, {
                    productType: ParkingProductType.PedestrianAccess,
                })
            );
            return;
        }

        dispatch(
            parkingProductsThunks.getDelegeesThunk({
                request,
                productContractTypeFilter: { productType: ParkingProductType.ParkingRights },
            })
        );
    };

    useOnBulkImportFinishedCallback(fetchDelegees);

    const [fetchKey, setFetchKey] = useState(0);
    useEffect(() => {
        fetchDelegees();
    }, [
        appState.user.seasonTicketOwnerCrmId,
        dispatch,
        productType,
        enableDelegationsScheduling,
        appState.selectedLanguage,
        dataTableContext.state.pageNumber,
        dataTableContext.state.pageSize,
        fetchKey,
        myFleetContext.myFleetContextState.orderDesc,
        myFleetContext.myFleetContextState.sortBy,
    ]);

    const sharedProductActions = useSharedProductsMenuAction();

    const columns: IFilteredColumn<IDelegeeDataTableModel>[] =
        filteredColumnObjectBuilder.buildDelegeesColumns(
            t,
            getParkingProductOptions(parkingProducts.parkingProducts?.data ?? [], productType),
            enableDelegationsScheduling
        );

    const getTableRows = (): IDelegeeDataTableModel[] => {
        if (enableDelegationsScheduling) {
            const pageData = entryRightOverview.pages[dataTableContext.state.pageNumber!]?.data;
            return (pageData ?? []).map(createOverviewData);
        }

        const delegees =
            productType === ParkingProductType.PedestrianAccess
                ? groupedEntryRightDelegees
                : groupedParkingDelegees;

        if (
            !isNull(delegees.pages) &&
            !isUndefined(delegees.pages) &&
            !isUndefined(delegees.pages[dataTableContext.state.pageNumber!])
        ) {
            return delegees.pages[dataTableContext.state.pageNumber!].data?.map(
                createGroupedData
            ) as IDelegeeDataTableModel[];
        }

        return [];
    };

    const getLoadingState = () => {
        if (enableDelegationsScheduling) {
            return entryRightOverview.pages[dataTableContext.state.pageNumber!]?.loading ?? false;
        }

        const delegees =
            productType === ParkingProductType.PedestrianAccess
                ? groupedEntryRightDelegees
                : groupedParkingDelegees;

        if (
            !isNull(delegees.pages) &&
            !isUndefined(delegees.pages) &&
            !isUndefined(delegees.pages[dataTableContext.state.pageNumber!])
        ) {
            return delegees.pages[dataTableContext.state.pageNumber!].loading;
        }

        return false;
    };

    const rows: IDelegeeDataTableModel[] = getTableRows();

    const getRegistrationIdByTableRow = (focusedRow: IDataTableRow | null): string | null => {
        const rowKey = focusedRow?.rowKey;
        if (typeof rowKey === "string") {
            return rowKey;
        }

        return rowKey?.delegeeId ?? null;
    };

    const isResendInviteDisabled = (focusedRow: IDataTableRow | null) => {
        const delegeeRow = (focusedRow as IDelegeeDataTableModel) || null;

        return delegeeRow?.products == null || delegeeRow.status.props?.type !== Statuses.Pending;
    };

    const onSort = (event: React.MouseEvent<unknown>, property: keyof IDelegeeDataTableModel) => {
        myFleetContext.setSorting(property.toString());
    };

    const totalPages = enableDelegationsScheduling
        ? entryRightOverview.totalPages
        : productType === ParkingProductType.PedestrianAccess
        ? groupedEntryRightDelegees.totalPages
        : groupedParkingDelegees.totalPages;

    const selectedDelegeesCount = useMemo(() => {
        if (selectedRowKeys.length === 0) {
            return 0;
        }

        if (isEntryRightsMyProductsRowKeyArray(selectedRowKeys)) {
            return new Set(selectedRowKeys.map((x) => x.delegeeId)).size;
        }

        return selectedRowKeys.length;
    }, [selectedRowKeys]);

    return (
        <FleetManagerDataTable
            subHeading={props.subHeading}
            columns={columns}
            rows={rows}
            isLoading={getLoadingState()}
            totalPages={totalPages}
            getRegistrationIdFromTableRow={getRegistrationIdByTableRow}
            isResendInviteDisabled={isResendInviteDisabled}
            resendAll={!enableDelegationsScheduling}
            onFilterClicked={() => setFetchKey(prev => prev + 1)}
            onResetFiltersClicked={() => setFetchKey(prev => prev + 1)}
            onSort={onSort}
            orderBy={myFleetContext.myFleetContextState.sortBy ?? ""}
            order={myFleetContext.myFleetContextState.orderDesc ? "desc" : "asc"}
            translationOverrideKey={"delegees"}
            productType={productType}
            heading={{
                options: {
                    useHeading: true,
                    showHeadingCount: false,
                },
                title: t("myFleet"),
            }}
            filtering={{
                options: {
                    useFilters: true,
                },
            }}
            actions={{
                options: {
                    useActions: true,
                },
                add: {
                    useAdd: !sharedProductActions.menu.useMenu,
                    showAdd: !sharedProductActions.menu.showMenu,
                    addLabel: t("fleetManagerTable:addDelegee"),
                    onAddClicked: sharedProductActions.onAddDelegee,
                },
                delete: {
                    useDelete: true,
                    showDelete: selectedDelegeesCount > 0,
                    deleteLabel: `${t("globals:delete")} (${selectedDelegeesCount})`,
                    onDeleteClicked: onDeleteClicked,
                },
                revoke: {
                    useRevoke: true,
                    showRevoke: selectedParkingRightsCount > 0,
                    revokeLabel: `${t("globals:revoke")} (${selectedParkingRightsCount})`,
                    onRevokeClicked: onRevokeClicked,
                },
                menu: sharedProductActions.menu,
            }}
        />
    );
};

export default DelegeesDataTable;
